/**
 * What a run on a live dataset is doing right now: pulling its traces, then
 * scoring them. Traces arrive all at once, so the bar follows the scoring run
 * (checks scored, failed, skipped), never the imported count.
 */

import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/page";
import { errorText } from "@/components/ui/states";
import { formatCount, plural } from "@/lib/format";
import { useUiStore } from "@/stores/ui-store";
import { ProjectAction } from "@/features/projects/project-action";
import { useExperimentAction, useScoringTally } from "../hooks";
import { finishedChecks, tallyParts, type ScoringTally } from "../scoring/scoring-tally";
import { isLive } from "../status";
import type { ExperimentSummary } from "../types";

export function TraceRunStatus({ experiment: e }: { experiment: ExperimentSummary }) {
  const live = isLive(e.status);
  const tally = useScoringTally(e);
  if (e.status === "collecting") return <CollectingTraces experiment={e} />;
  if (!live) return null;
  const done = tally ? finishedChecks(tally) : 0;
  return (
    <section className="box flex flex-col gap-2 px-4 py-3.5" aria-label="Progress">
      <span className="text-[13px]">
        {e.status === "stopping" && "Stopping · "}
        {headline(e.status, tally, done)}
      </span>
      <Progress value={done} max={tally?.total ?? 0} label="Scoring progress" />
      {tally && done > 0 && (
        <span className="text-xs" aria-label="Scoring breakdown">
          {tallyParts(tally, true).join(" · ")}
        </span>
      )}
      <span className="text-xs text-muted">You can close this page. Scores fill in as checks finish.</span>
    </section>
  );
}

/** Traces are being pulled into a new version; an agent run or the scoring starts once they arrive.
 *  Stopping now ends the run at once: nothing has been run or scored yet. */
export function CollectingTraces({
  experiment: e,
}: {
  experiment: Pick<ExperimentSummary, "id" | "runner" | "pull">;
}) {
  const qflow = e.pull?.connection.connector_type === "qflow";
  const action = useExperimentAction(e.id);
  const notify = useUiStore((s) => s.notify);
  const next = e.runner === "agent" ? "The agent runs on them" : "Scoring starts";
  return (
    <section className="box flex items-center gap-5 px-4 py-3.5" aria-label="Progress">
      <div className="flex flex-1 flex-col gap-1">
        <span className="text-[13px]">
          {qflow ? "Collecting runs from qFlow" : "Collecting traces from Langfuse"} into a new dataset
          version…
        </span>
        <span className="text-xs text-muted">You can close this page. {next} once they arrive.</span>
      </div>
      <ProjectAction permission="experiments:write">
        <Button
          disabled={action.isPending}
          onClick={() =>
            action.mutate("stop", {
              onSuccess: () => notify("Stopped. No traces were pulled."),
              onError: (err) => notify(errorText(err, "That did not work. Try again."), "error"),
            })
          }
        >
          Stop
        </Button>
      </ProjectAction>
    </section>
  );
}

function headline(status: ExperimentSummary["status"], tally: ScoringTally | null, done: number) {
  if (status === "aggregating") return "Computing final scores";
  if (!tally || tally.total === 0) return "Waiting for scoring to start";
  if (done >= tally.total) return "Every check is finished · computing final scores";
  if (done === 0) return `Scoring ${plural(tally.total, "check")} · not started yet`;
  return (
    <>
      Scoring <strong className="font-mono">{formatCount(done)}</strong> of {plural(tally.total, "check")}
    </>
  );
}
